import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../../contexts/ToastContext';
import ComandaService from '../../../services/ComandaService';
import AutorizacionModal from '../../Auth/AutorizacionModal';
import './FormaPago.css';

const METODOS_PAGO = [
  { key: 'efectivo', icon: '\u{1F4B5}', label: 'Efectivo', shortcut: 'F1', color: '#10b981', requiresAuth: false },
  { key: 'tarjeta', icon: '\u{1F4B3}', label: 'Tarjeta', shortcut: 'F2', color: '#3b82f6', requiresAuth: false },
  { key: 'transferencia', icon: '\u{1F3E6}', label: 'Transfer.', shortcut: 'F3', color: '#8b5cf6', requiresAuth: true },
  { key: 'cxc', icon: '\u{1F4DD}', label: 'CxC', shortcut: 'F4', color: '#f59e0b', requiresAuth: false },
  { key: 'cupon', icon: '\u{1F3AB}', label: 'Cupon', shortcut: 'F5', color: '#ef4444', requiresAuth: true },
  { key: 'cortesia', icon: '\u{1F381}', label: 'Cortesia', shortcut: 'F6', color: '#6b7280', requiresAuth: true }
];

const MONTOS_RAPIDOS = [50, 100, 200, 500];

const FormaPago = ({
  visible,
  onClose,
  infoPedido,
  productos,
  onPagoCompletado,
  tipoPedido = 'mesa',
  configuracion = {},
  totalCuenta: totalCuentaProp,
  pedidoInfo: pedidoInfoProp,
  idPedido: idPedidoProp,
  onProcesarPago,
  onCancelar,
  permitirMultiplesFormas = true,
  mostrarDescuentos = true,
  descuentosPromociones: descuentosPromocionesProp = 0,
  promocionesAplicadas: promocionesAplicadasProp = [],
  ...otherProps
}) => {
  const toast = useToast();
  const navigate = useNavigate();

  const [datosPago, setDatosPago] = useState(() => {
    const datosGuardados = localStorage.getItem('datosPago');
    if (datosGuardados) {
      try {
        return JSON.parse(datosGuardados);
      } catch (error) {
        console.error('Error al parsear datos de pago:', error);
        return null;
      }
    }
    return null;
  });

  const totalCuenta = totalCuentaProp ||
    datosPago?.totalCuenta ||
    datosPago?.total ||
    infoPedido?.total ||
    productos?.reduce((sum, p) => {
      if (p.subtotal !== undefined && p.subtotal !== null) {
        return sum + parseFloat(p.subtotal);
      }
      return sum + (parseFloat(p.precio) * parseInt(p.cantidad));
    }, 0) ||
    0;

  const pedidoInfo = pedidoInfoProp || infoPedido || datosPago?.pedidoInfo || {};

  const idPedido = idPedidoProp ||
    datosPago?.idPedido ||
    infoPedido?.idPedido ||
    pedidoInfo?.idPedido ||
    null;

  const descuentosPromociones = descuentosPromocionesProp ||
    datosPago?.descuentos ||
    pedidoInfo?.descuentos ||
    0;

  const [pagosAplicados, setPagosAplicados] = useState([]);
  const [metodoPagoSeleccionado, setMetodoPagoSeleccionado] = useState('efectivo');
  const [montoPago, setMontoPago] = useState('');
  const [observacionesPago, setObservacionesPago] = useState('');
  const [procesandoPago, setProcesandoPago] = useState(false);
  const [descuentoGlobal, setDescuentoGlobal] = useState(descuentosPromociones);
  const [tipoDescuentoGlobal, setTipoDescuentoGlobal] = useState(descuentosPromociones > 0 ? 'monto' : 'porcentaje');
  const [mostrarModalCambio, setMostrarModalCambio] = useState(false);
  const [datosCambio, setDatosCambio] = useState({ cambio: 0, totalPagado: 0, total: 0 });

  const [datosTarjeta, setDatosTarjeta] = useState({
    numeroTarjeta: '', tipoTarjeta: 'credito', referencia: '', autorizacion: '', terminal: ''
  });

  const [datosTransferencia, setDatosTransferencia] = useState({
    banco: '', referencia: '', numeroOperacion: '', autorizadoPor: '', metodoAuth: '', razon: '', autorizado: false
  });

  const [datosCxC, setDatosCxC] = useState({
    clienteId: '', clienteNombre: '', limiteCredito: 0, saldoActual: 0
  });

  const [datosCupon, setDatosCupon] = useState({
    codigoCupon: '', valorCupon: 0, tipoDescuento: 'porcentaje', autorizadoPor: '', metodoAuth: '', razon: '', autorizado: false
  });

  const [datosCortesia, setDatosCortesia] = useState({
    autorizadoPor: '', metodoAuth: '', razon: '', autorizado: false
  });

  const [mostrarAutorizacion, setMostrarAutorizacion] = useState(false);
  const [autorizacionPendiente, setAutorizacionPendiente] = useState(null);
  const [abriendoCajon, setAbriendoCajon] = useState(false);

  const montoInputRef = useRef(null);

  const subtotalProductos = useMemo(() => {
    if (productos && Array.isArray(productos)) {
      return productos.reduce((sum, producto) => {
        if (producto.subtotal !== undefined && producto.subtotal !== null) {
          return sum + parseFloat(producto.subtotal);
        }
        const precio = parseFloat(producto.precio) || 0;
        const cantidad = parseInt(producto.cantidad) || 0;
        return sum + (precio * cantidad);
      }, 0);
    }
    return totalCuenta;
  }, [productos, totalCuenta]);

  const descuentoCalculado = useMemo(() => {
    if (tipoDescuentoGlobal === 'porcentaje') {
      return (subtotalProductos * descuentoGlobal) / 100;
    }
    return Math.min(descuentoGlobal, subtotalProductos);
  }, [subtotalProductos, descuentoGlobal, tipoDescuentoGlobal]);

  const totalConDescuento = subtotalProductos - descuentoCalculado;

  const totalPagado = useMemo(() => {
    return pagosAplicados.reduce((sum, pago) => sum + parseFloat(pago.monto), 0);
  }, [pagosAplicados]);

  const saldoPendiente = totalConDescuento - totalPagado;
  const cambio = totalPagado > totalConDescuento ? totalPagado - totalConDescuento : 0;
  const porcentajePagado = totalConDescuento > 0
    ? Math.min((totalPagado / totalConDescuento) * 100, 100)
    : 0;

  const limpiarFormulario = useCallback(() => {
    setMontoPago('');
    setObservacionesPago('');
    setDatosTarjeta({ numeroTarjeta: '', tipoTarjeta: 'credito', referencia: '', autorizacion: '', terminal: '' });
    setDatosTransferencia({ banco: '', referencia: '', numeroOperacion: '', autorizadoPor: '', metodoAuth: '', razon: '', autorizado: false });
    setDatosCupon({ codigoCupon: '', valorCupon: 0, tipoDescuento: 'porcentaje', autorizadoPor: '', metodoAuth: '', razon: '', autorizado: false });
    setDatosCortesia({ autorizadoPor: '', metodoAuth: '', razon: '', autorizado: false });
  }, []);

  const validarPago = useCallback(() => {
    const monto = parseFloat(montoPago);
    if (!monto || monto <= 0) { toast.error('Ingrese un monto valido'); return false; }
    if (metodoPagoSeleccionado === 'tarjeta' && !datosTarjeta.terminal) { toast.error('Debe seleccionar una terminal'); return false; }
    if (metodoPagoSeleccionado === 'transferencia') {
      if (!datosTransferencia.autorizado) { toast.error('La transferencia debe estar autorizada'); return false; }
      if (!datosTransferencia.banco.trim()) { toast.error('Seleccione el banco'); return false; }
      if (!datosTransferencia.referencia.trim()) { toast.error('Ingrese la referencia de transferencia'); return false; }
    }
    if (metodoPagoSeleccionado === 'cxc' && !datosCxC.clienteId) { toast.error('Seleccione un cliente para credito'); return false; }
    if (metodoPagoSeleccionado === 'cupon') {
      if (!datosCupon.autorizado) { toast.error('El cupon debe estar autorizado'); return false; }
      if (!datosCupon.codigoCupon.trim()) { toast.error('Ingrese el codigo del cupon'); return false; }
    }
    if (metodoPagoSeleccionado === 'cortesia' && !datosCortesia.autorizado) { toast.error('La cortesia debe estar autorizada'); return false; }
    return true;
  }, [montoPago, metodoPagoSeleccionado, datosTarjeta, datosTransferencia, datosCxC, datosCupon, datosCortesia, toast]);

  const agregarPago = useCallback(() => {
    if (!validarPago()) return;
    const monto = parseFloat(montoPago);
    const nuevoPago = {
      id: Date.now(), metodo: metodoPagoSeleccionado, monto, observaciones: observacionesPago,
      fecha: new Date().toISOString(), datos: {}
    };
    switch (metodoPagoSeleccionado) {
      case 'tarjeta': nuevoPago.datos = { ...datosTarjeta }; break;
      case 'transferencia':
        nuevoPago.datos = { ...datosTransferencia };
        nuevoPago.observaciones = datosTransferencia.razon || `Transferencia autorizada por ${datosTransferencia.autorizadoPor}`;
        break;
      case 'cxc': nuevoPago.datos = { ...datosCxC }; break;
      case 'cupon':
        nuevoPago.datos = { ...datosCupon };
        nuevoPago.observaciones = datosCupon.razon || `Cupon autorizado por ${datosCupon.autorizadoPor}`;
        break;
      case 'cortesia':
        nuevoPago.datos = { ...datosCortesia };
        nuevoPago.observaciones = datosCortesia.razon || `Cortesia autorizada por ${datosCortesia.autorizadoPor}`;
        break;
      default: break;
    }
    setPagosAplicados(prev => [...prev, nuevoPago]);
    limpiarFormulario();
    toast.success(`Pago de $${monto.toFixed(2)} agregado`);
  }, [validarPago, montoPago, metodoPagoSeleccionado, observacionesPago, datosTarjeta, datosTransferencia, datosCxC, datosCupon, datosCortesia, limpiarFormulario, toast]);

  const eliminarPago = useCallback((pagoId) => {
    setPagosAplicados(prev => prev.filter(pago => pago.id !== pagoId));
    toast.success('Pago eliminado');
  }, [toast]);

  const procesarPagoCompleto = useCallback(async () => {
    if (pagosAplicados.length === 0) { toast.error('Debe agregar al menos un metodo de pago'); return; }
    if (saldoPendiente > 0.01) { toast.error('El pago no esta completo. Faltan $' + saldoPendiente.toFixed(2)); return; }
    setProcesandoPago(true);
    try {
      const descuentoManual = Math.max(0, descuentoCalculado - (descuentosPromociones || 0));
      const datosFacturacion = {
        idPedido, subtotal: subtotalProductos, descuentoGlobal: descuentoCalculado,
        descuentoManual,
        total: totalConDescuento, pagos: pagosAplicados, cambio, tipoPedido, productos, infoPedido
      };
      if (onProcesarPago && typeof onProcesarPago === 'function') {
        await onProcesarPago(datosFacturacion);
      } else {
        await ComandaService.procesarPago(datosFacturacion);
      }
      toast.success('Pago procesado correctamente');
      const hayPagoEfectivo = pagosAplicados.some(pago => pago.metodo === 'efectivo');
      if (hayPagoEfectivo) {
        ComandaService.abrirCajon().catch(err => console.error('Error al abrir cajón automático:', err));
      }
      if (cambio > 0 && hayPagoEfectivo) {
        setDatosCambio({ cambio, totalPagado, total: totalConDescuento });
        setMostrarModalCambio(true);
      } else {
        procederConCierre();
      }
      function procederConCierre() {
        if (onPagoCompletado) onPagoCompletado(datosFacturacion);
        localStorage.removeItem('datosPago');
        if (onClose) onClose(); else if (onCancelar) onCancelar();
        setTimeout(() => { navigate('/ventas'); }, 500);
      }
    } catch (error) {
      console.error('Error al procesar pago:', error);
      toast.error('Error al procesar el pago: ' + (error.message || 'Error desconocido'));
    } finally {
      setProcesandoPago(false);
    }
  }, [pagosAplicados, saldoPendiente, idPedido, subtotalProductos, descuentoCalculado, totalConDescuento, cambio, totalPagado, tipoPedido, productos, infoPedido, onProcesarPago, onPagoCompletado, onClose, onCancelar, navigate, toast]);

  // Effects

  // Escala proporcional: diseño base 1920x1080. Calcula min(vw/1920, vh/1080)
  // para que respete ancho Y alto (las media queries CSS no pueden hacer esto).
  useEffect(() => {
    const updateScale = () => {
      const scale = Math.min(window.innerWidth / 1920, window.innerHeight / 1080);
      document.documentElement.style.setProperty('--fp-scale', String(scale));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);

  useEffect(() => {
    if (visible !== undefined && visible) {
      setPagosAplicados([]);
      setDescuentoGlobal(descuentosPromociones || 0);
      setTipoDescuentoGlobal(descuentosPromociones > 0 ? 'monto' : 'porcentaje');
      limpiarFormulario();
      [100, 300, 500, 1000].forEach(delay => {
        setTimeout(() => {
          if (montoInputRef.current && document.activeElement !== montoInputRef.current) {
            montoInputRef.current.focus();
            montoInputRef.current.select();
          }
        }, delay);
      });
    }
  }, [visible, limpiarFormulario, descuentosPromociones]);

  useEffect(() => {
    if (descuentosPromociones > 0) {
      setDescuentoGlobal(descuentosPromociones);
      setTipoDescuentoGlobal('monto');
    }
  }, [descuentosPromociones]);

  useEffect(() => {
    if (visible && (metodoPagoSeleccionado !== 'tarjeta' || datosTarjeta.terminal)) {
      requestAnimationFrame(() => {
        setTimeout(() => {
          if (montoInputRef.current && document.activeElement !== montoInputRef.current) {
            montoInputRef.current.focus();
            montoInputRef.current.select();
          }
        }, 50);
      });
    }
  }, [metodoPagoSeleccionado, datosTarjeta.terminal, visible]);

  // Enter key for change modal
  useEffect(() => {
    if (!mostrarModalCambio) return;
    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setMostrarModalCambio(false);
        if (onPagoCompletado) {
          const descuentoManual = Math.max(0, descuentoCalculado - (descuentosPromociones || 0));
          onPagoCompletado({
            idPedido, subtotal: subtotalProductos, descuentoGlobal: descuentoCalculado,
            descuentoManual,
            total: totalConDescuento, pagos: pagosAplicados, cambio: datosCambio.cambio, tipoPedido, productos, infoPedido
          });
        }
        localStorage.removeItem('datosPago');
        if (onClose) onClose(); else if (onCancelar) onCancelar();
        setTimeout(() => { navigate('/ventas'); }, 500);
      }
    };
    document.addEventListener('keydown', handleKeyPress);
    return () => document.removeEventListener('keydown', handleKeyPress);
  }, [mostrarModalCambio, onPagoCompletado, onClose, onCancelar, navigate, idPedido, subtotalProductos, descuentoCalculado, totalConDescuento, pagosAplicados, datosCambio, tipoPedido, productos, infoPedido]);

  const autoLlenarSaldoPendiente = useCallback(() => {
    if (saldoPendiente > 0) setMontoPago(saldoPendiente.toFixed(2));
  }, [saldoPendiente]);

  const handleCancelar = useCallback(() => {
    if (onCancelar) onCancelar(); else if (onClose) onClose();
  }, [onCancelar, onClose]);

  const solicitarAutorizacion = useCallback((tipo) => {
    setAutorizacionPendiente({ monto: saldoPendiente > 0 ? saldoPendiente : 0, tipo });
    setMostrarAutorizacion(true);
  }, [saldoPendiente]);

  const handleAutorizacionExitosa = useCallback((authData) => {
    if (!autorizacionPendiente) return;
    const tipoMetodo = autorizacionPendiente.tipo;
    setMetodoPagoSeleccionado(tipoMetodo);
    setAutorizacionPendiente(null);
    setMostrarAutorizacion(false);
    let mensaje = '';
    switch (tipoMetodo) {
      case 'cortesia':
        mensaje = 'Cortesia';
        setDatosCortesia({ autorizadoPor: authData.usuario.usuario || authData.usuario.nombre, metodoAuth: authData.metodoAuth, razon: authData.razon, autorizado: true });
        break;
      case 'cupon':
        mensaje = 'Cupon';
        setDatosCupon(prev => ({ ...prev, autorizadoPor: authData.usuario.usuario || authData.usuario.nombre, metodoAuth: authData.metodoAuth, razon: authData.razon || '', autorizado: true }));
        break;
      case 'transferencia':
        mensaje = 'Transferencia';
        setDatosTransferencia(prev => ({ ...prev, autorizadoPor: authData.usuario.usuario || authData.usuario.nombre, metodoAuth: authData.metodoAuth, razon: authData.razon || '', autorizado: true }));
        break;
      default: mensaje = 'Metodo de pago';
    }
    toast.success(`${mensaje} autorizada por ${authData.usuario.usuario || authData.usuario.nombre}. Ingrese los datos.`);
    setTimeout(() => { if (montoInputRef.current) montoInputRef.current.focus(); }, 100);
  }, [autorizacionPendiente, toast]);

  const handleAutorizacionCancelada = useCallback(() => {
    setAutorizacionPendiente(null);
    setMostrarAutorizacion(false);
  }, []);

  const handleAbrirCajon = useCallback(async () => {
    if (abriendoCajon) return;
    setAbriendoCajon(true);
    try {
      const resultado = await ComandaService.abrirCajon();
      if (resultado.success) {
        toast.success('Cajón abierto');
      } else {
        toast.error(resultado.message || 'No se pudo abrir el cajón');
      }
    } catch (error) {
      toast.error('Error al abrir el cajón: ' + error.message);
    } finally {
      setAbriendoCajon(false);
    }
  }, [abriendoCajon, toast]);

  const forzarFocusEnInput = useCallback(() => {
    if (montoInputRef.current && visible) {
      montoInputRef.current.focus();
      requestAnimationFrame(() => {
        if (montoInputRef.current) { montoInputRef.current.focus(); montoInputRef.current.select(); }
      });
    }
  }, [visible]);

  // Keyboard shortcuts
  useEffect(() => {
    // Si visible esta definido y es false, no activar atajos. Si es undefined, el componente esta visible.
    if ((visible !== undefined && !visible) || mostrarAutorizacion || mostrarModalCambio) return;
    const handleKeyDown = (e) => {
      const tag = document.activeElement?.tagName;
      if (tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (tag === 'INPUT' && document.activeElement !== montoInputRef.current) return;
      switch (e.key) {
        case 'F1': e.preventDefault(); setMetodoPagoSeleccionado('efectivo'); break;
        case 'F2': e.preventDefault(); setMetodoPagoSeleccionado('tarjeta'); break;
        case 'F3': e.preventDefault(); solicitarAutorizacion('transferencia'); break;
        case 'F4': e.preventDefault(); setMetodoPagoSeleccionado('cxc'); break;
        case 'F5': e.preventDefault(); solicitarAutorizacion('cupon'); break;
        case 'F6': e.preventDefault(); solicitarAutorizacion('cortesia'); break;
        case 'F7': e.preventDefault(); handleAbrirCajon(); break;
        case 'F8': e.preventDefault(); autoLlenarSaldoPendiente(); break;
        case 'F12': e.preventDefault(); procesarPagoCompleto(); break;
        case 'Escape': e.preventDefault(); handleCancelar(); break;
        default: break;
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [visible, mostrarAutorizacion, mostrarModalCambio, solicitarAutorizacion, handleAbrirCajon, autoLlenarSaldoPendiente, procesarPagoCompleto, handleCancelar]);

  const cerrarModalCambio = useCallback(() => {
    setMostrarModalCambio(false);
    if (onPagoCompletado) {
      const descuentoManual = Math.max(0, descuentoCalculado - (descuentosPromociones || 0));
      onPagoCompletado({ idPedido, subtotal: subtotalProductos, descuentoGlobal: descuentoCalculado, descuentoManual, total: totalConDescuento, pagos: pagosAplicados, cambio, tipoPedido, productos, infoPedido });
    }
    localStorage.removeItem('datosPago');
    if (onClose) onClose(); else if (onCancelar) onCancelar();
    setTimeout(() => { navigate('/ventas'); }, 500);
  }, [onPagoCompletado, idPedido, subtotalProductos, descuentoCalculado, totalConDescuento, pagosAplicados, cambio, tipoPedido, productos, infoPedido, onClose, onCancelar, navigate]);

  if (visible !== undefined && !visible) return null;

  const isFinalizarDisabled = procesandoPago || pagosAplicados.length === 0 || saldoPendiente > 0.01;

  return (
    <div className="fp-overlay" onClick={forzarFocusEnInput}>
      <div className="fp-container" onClick={(e) => e.stopPropagation()}>

        {/* Header */}
        <div className="fp-header">
          <div className="fp-header__left">
            <div className="fp-header__icon">{'\u{1F4B3}'}</div>
            <div>
              <h1 className="fp-header__title">Sistema de Pagos</h1>
              <p className="fp-header__subtitle">Pedido #{idPedido || 'N/A'} - Total: ${totalConDescuento.toFixed(2)}</p>
              <span className="fp-header__method-badge">
                {METODOS_PAGO.find(m => m.key === metodoPagoSeleccionado)?.icon}{' '}
                {METODOS_PAGO.find(m => m.key === metodoPagoSeleccionado)?.label}
              </span>
            </div>
          </div>
          <div className="fp-header__shortcuts">
            {[
              { key: 'F1-F6', label: 'Metodo' },
              { key: 'F7', label: 'Cajon' },
              { key: 'F8', label: 'Saldo' },
              { key: 'Enter', label: 'Agregar' },
              { key: 'F12', label: 'Finalizar' },
              { key: 'Esc', label: 'Cerrar' }
            ].map(s => (
              <span key={s.key} className="fp-header__shortcut-item">
                <span className="fp-header__shortcut-key">{s.key}</span>
                {s.label}
              </span>
            ))}
          </div>
          <button className="fp-header__close" onClick={handleCancelar} title="Cerrar (Esc)">{'\u00D7'}</button>
        </div>

        {/* Main Content */}
        <div className="fp-main">

          {/* LEFT COLUMN */}
          <div className="fp-left">
            <div className="fp-card">
              <div className="fp-section-header">
                <div className="fp-section-header__icon">{'\u2795'}</div>
                <h2 className="fp-section-header__title">Agregar Forma de Pago</h2>
              </div>

              <label className="fp-methods-label">Metodo de Pago</label>
              <div className="fp-methods">
                {METODOS_PAGO.map(metodo => (
                  <button
                    key={metodo.key}
                    className={`fp-method-btn fp-method-btn--${metodo.key} ${metodoPagoSeleccionado === metodo.key ? 'fp-method-btn--active' : ''}`}
                    onClick={() => {
                      if (metodo.requiresAuth) solicitarAutorizacion(metodo.key);
                      else setMetodoPagoSeleccionado(metodo.key);
                    }}
                  >
                    <span className="fp-shortcut">{metodo.shortcut}</span>
                    <span className="fp-method-btn__icon">{metodo.icon}</span>
                    <span>{metodo.label}</span>
                  </button>
                ))}
              </div>

              {/* Amount */}
              {(metodoPagoSeleccionado !== 'tarjeta' || datosTarjeta.terminal) && (
                <div className="fp-amount">
                  <label className="fp-amount__label">Monto</label>
                  <div className="fp-amount__row">
                    <div className="fp-amount__input-wrap">
                      <span className="fp-amount__currency">$</span>
                      <input
                        ref={montoInputRef}
                        type="number"
                        value={montoPago}
                        onChange={(e) => setMontoPago(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); agregarPago(); } }}
                        onFocus={(e) => e.target.select()}
                        placeholder="0.00"
                        step="0.01"
                        className="fp-amount__input"
                        autoFocus={visible && (metodoPagoSeleccionado !== 'tarjeta' || datosTarjeta.terminal)}
                      />
                    </div>
                    <button className="fp-saldo-btn" onClick={autoLlenarSaldoPendiente}>
                      Saldo <span className="fp-btn-shortcut">F8</span>
                    </button>
                  </div>
                  {metodoPagoSeleccionado === 'efectivo' && (
                    <div className="fp-quick-amounts">
                      {MONTOS_RAPIDOS.map(monto => (
                        <button key={monto} className="fp-quick-btn" onClick={() => setMontoPago(monto.toString())}>
                          ${monto}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Terminal */}
              {metodoPagoSeleccionado === 'tarjeta' && (
                <div className="fp-terminal">
                  <label className="fp-terminal__label">Seleccionar Terminal:</label>
                  <div className="fp-terminal__grid">
                    {['Terminal B', 'Terminal X'].map(terminal => (
                      <button
                        key={terminal}
                        type="button"
                        onClick={() => setDatosTarjeta(prev => ({ ...prev, terminal }))}
                        className={`fp-terminal__btn ${datosTarjeta.terminal === terminal ? 'fp-terminal__btn--active' : ''}`}
                      >
                        {'\u{1F5A5}\uFE0F'} {terminal}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Transfer fields */}
              {metodoPagoSeleccionado === 'transferencia' && datosTransferencia.autorizado && (
                <div className="fp-method-fields fp-method-fields--transferencia">
                  <h4 className="fp-method-fields__title fp-method-fields__title--transferencia">{'\u{1F3E6}'} Datos de Transferencia (Autorizada)</h4>
                  <div className="fp-method-fields__auth-info fp-method-fields__auth-info--transferencia">
                    <div><strong>Autorizado por:</strong> {datosTransferencia.autorizadoPor}</div>
                    {datosTransferencia.razon && <div><strong>Motivo:</strong> {datosTransferencia.razon}</div>}
                  </div>
                  <div className="fp-method-fields__grid">
                    <select value={datosTransferencia.banco} onChange={(e) => setDatosTransferencia(prev => ({ ...prev, banco: e.target.value }))} className="fp-method-fields__select fp-method-fields__select--transferencia">
                      <option value="">Seleccionar banco</option>
                      <option value="bancomer">Bancomer</option>
                      <option value="banamex">Banamex</option>
                      <option value="santander">Santander</option>
                      <option value="hsbc">HSBC</option>
                      <option value="banorte">Banorte</option>
                    </select>
                    <input type="text" placeholder="Referencia" value={datosTransferencia.referencia} onChange={(e) => setDatosTransferencia(prev => ({ ...prev, referencia: e.target.value }))} className="fp-method-fields__input fp-method-fields__input--transferencia" />
                  </div>
                </div>
              )}

              {/* Cupon fields */}
              {metodoPagoSeleccionado === 'cupon' && datosCupon.autorizado && (
                <div className="fp-method-fields fp-method-fields--cupon">
                  <h4 className="fp-method-fields__title fp-method-fields__title--cupon">{'\u{1F3AB}'} Datos del Cupon (Autorizado)</h4>
                  <div className="fp-method-fields__auth-info fp-method-fields__auth-info--cupon">
                    <div><strong>Autorizado por:</strong> {datosCupon.autorizadoPor}</div>
                    {datosCupon.razon && <div><strong>Motivo:</strong> {datosCupon.razon}</div>}
                  </div>
                  <input type="text" placeholder="Codigo del cupon" value={datosCupon.codigoCupon} onChange={(e) => setDatosCupon(prev => ({ ...prev, codigoCupon: e.target.value }))} className="fp-method-fields__input fp-method-fields__input--cupon" />
                </div>
              )}

              {/* Cortesia info */}
              {metodoPagoSeleccionado === 'cortesia' && datosCortesia.autorizado && (
                <div className="fp-method-fields fp-method-fields--cortesia">
                  <h4 className="fp-method-fields__title fp-method-fields__title--cortesia">{'\u2705'} Cortesia Autorizada</h4>
                  <div className="fp-method-fields__auth-info fp-method-fields__auth-info--cortesia">
                    <div><strong>Autorizado por:</strong> {datosCortesia.autorizadoPor}</div>
                    <div><strong>Metodo:</strong> {datosCortesia.metodoAuth === 'biometric' ? 'Huella Digital' : 'Contrasena'}</div>
                    {datosCortesia.razon && <div><strong>Motivo:</strong> {datosCortesia.razon}</div>}
                  </div>
                </div>
              )}

              <button className="fp-add-btn" onClick={agregarPago} disabled={!montoPago || parseFloat(montoPago) <= 0}>
                <span>{'\u2795'}</span> Agregar Pago <span className="fp-btn-shortcut">Enter</span>
              </button>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="fp-right">
            <div className="fp-card fp-card--right">
              <h3 className="fp-section-header__title fp-section-header__title--summary">
                <span style={{ fontSize: '20px' }}>{'\u{1F4CA}'}</span> Resumen del Pedido
              </h3>
              <div className="fp-summary">
                <div className="fp-summary__row">
                  <span className="fp-summary__row-label">Subtotal:</span>
                  <span className="fp-summary__row-value">${subtotalProductos.toFixed(2)}</span>
                </div>
                {descuentoCalculado > 0 && (
                  <div className="fp-summary__row fp-summary__row--descuento">
                    <span className="fp-summary__row-label">Descuento:</span>
                    <span className="fp-summary__row-value">-${descuentoCalculado.toFixed(2)}</span>
                  </div>
                )}
                <div className="fp-summary__divider"></div>
                <div className="fp-summary__grid">
                  <div className="fp-summary__row fp-summary__row--total">
                    <span className="fp-summary__row-label">Total:</span>
                    <span className="fp-summary__row-value">${totalConDescuento.toFixed(2)}</span>
                  </div>
                  <div className={`fp-summary__row fp-summary__row--pagado ${totalPagado > 0 ? 'fp-summary__row--pagado-active' : ''}`}>
                    <span className="fp-summary__row-label">Pagado:</span>
                    <span className="fp-summary__row-value">${totalPagado.toFixed(2)}</span>
                  </div>
                </div>
                <div className={`fp-summary__row ${saldoPendiente > 0.01 ? 'fp-summary__row--saldo-pending' : 'fp-summary__row--saldo-ok'}`}>
                  <span className="fp-summary__row-label">Saldo:</span>
                  <span className="fp-summary__row-value">${saldoPendiente.toFixed(2)}</span>
                </div>
                {cambio > 0 && (
                  <div className="fp-summary__row fp-summary__row--cambio">
                    <span className="fp-summary__row-label">{'\u{1F4B5}'} Cambio:</span>
                    <span className="fp-summary__row-value">${cambio.toFixed(2)}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="fp-progress">
              <div className="fp-progress__header">
                <span>Progreso de pago</span>
                <span>{porcentajePagado.toFixed(0)}%</span>
              </div>
              <div className="fp-progress__bar">
                <div className="fp-progress__fill" style={{ width: `${porcentajePagado}%` }}></div>
              </div>
            </div>

            <div className="fp-card fp-card--right fp-card--flex">
              <h3 className="fp-payments__title">
                <span style={{ fontSize: '20px' }}>{'\u{1F4B0}'}</span> Pagos Aplicados ({pagosAplicados.length})
              </h3>
              <div className="fp-payments__scroll">
                {pagosAplicados.length === 0 ? (
                  <div className="fp-payments__empty">
                    <div className="fp-payments__empty-icon">{'\u{1F4B3}'}</div>
                    No se han agregado pagos
                  </div>
                ) : (
                  <div className="fp-payments__list">
                    {pagosAplicados.map((pago) => (
                      <div key={pago.id} className={`fp-payment-item fp-payment-item--${pago.metodo}`}>
                        <div className="fp-payment-item__left">
                          <span className="fp-payment-item__icon">
                            {METODOS_PAGO.find(m => m.key === pago.metodo)?.icon}
                          </span>
                          <span className={`fp-payment-item__badge fp-payment-item__badge--${pago.metodo}`}>{pago.metodo}</span>
                          {pago.metodo === 'tarjeta' && pago.datos.numeroTarjeta && (
                            <span className="fp-payment-item__detail">****{pago.datos.numeroTarjeta.slice(-4)}</span>
                          )}
                          {pago.metodo === 'transferencia' && pago.datos.banco && (
                            <span className="fp-payment-item__detail">{pago.datos.banco}</span>
                          )}
                        </div>
                        <div className="fp-payment-item__right">
                          <span className="fp-payment-item__amount">${pago.monto.toFixed(2)}</span>
                          <button className="fp-payment-item__delete" onClick={() => eliminarPago(pago.id)} title="Eliminar pago">{'\u00D7'}</button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="fp-footer">
          <div className="fp-footer__warning">
            {saldoPendiente > 0.01 && <span>{'\u26A0\uFE0F'} Faltan ${saldoPendiente.toFixed(2)} por pagar</span>}
          </div>
          <div className="fp-footer__actions">
            <button className="fp-btn-cancel" onClick={handleCancelar} disabled={procesandoPago}>
              {'\u2716\uFE0F'} Cancelar <span className="fp-btn-shortcut">Esc</span>
            </button>
            <button className="fp-btn-drawer" onClick={handleAbrirCajon} disabled={abriendoCajon}>
              {abriendoCajon ? (
                <><div className="fp-spinner"></div> Abriendo...</>
              ) : (
                <><span>{'\u{1F4B0}'}</span> Abrir Cajon <span className="fp-btn-shortcut">F7</span></>
              )}
            </button>
            <button className="fp-btn-finalize" onClick={procesarPagoCompleto} disabled={isFinalizarDisabled}>
              {procesandoPago ? (
                <><div className="fp-spinner"></div> Procesando...</>
              ) : (
                <><span>{'\u{1F4B3}'}</span> Finalizar Pago <span className="fp-btn-shortcut">F12</span></>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Authorization Modal */}
      <AutorizacionModal
        isOpen={mostrarAutorizacion}
        onClose={handleAutorizacionCancelada}
        onAuthorize={handleAutorizacionExitosa}
        accion={
          autorizacionPendiente?.tipo === 'cortesia' ? 'aplicar cortesia' :
            autorizacionPendiente?.tipo === 'cupon' ? 'aplicar cupon' :
              autorizacionPendiente?.tipo === 'transferencia' ? 'aplicar transferencia' :
                'aplicar metodo de pago especial'
        }
        perfilRequerido="cortesias"
        titulo={
          autorizacionPendiente?.tipo === 'cortesia' ? 'Autorizacion de Cortesia' :
            autorizacionPendiente?.tipo === 'cupon' ? 'Autorizacion de Cupon' :
              autorizacionPendiente?.tipo === 'transferencia' ? 'Autorizacion de Transferencia' :
                'Autorizacion Requerida'
        }
        descripcion={
          autorizacionPendiente?.tipo === 'cortesia' ? 'Se requiere autorizacion para aplicar esta cortesia' :
            autorizacionPendiente?.tipo === 'cupon' ? 'Se requiere autorizacion para aplicar este cupon' :
              autorizacionPendiente?.tipo === 'transferencia' ? 'Se requiere autorizacion para aplicar esta transferencia' :
                'Se requiere autorizacion para continuar'
        }
        solicitarRazon={true}
        labelRazon={
          autorizacionPendiente?.tipo === 'cortesia' ? 'Motivo de la cortesia' :
            autorizacionPendiente?.tipo === 'cupon' ? 'Motivo del cupon' :
              autorizacionPendiente?.tipo === 'transferencia' ? 'Motivo de la transferencia' :
                'Motivo'
        }
      />

      {/* Change Modal */}
      {mostrarModalCambio && (
        <div className="fp-cambio-overlay">
          <div className="fp-cambio-modal">
            <div className="fp-cambio-modal__deco-top"></div>
            <div className="fp-cambio-modal__deco-bottom"></div>
            <div className="fp-cambio-modal__icon">
              <span className="fp-cambio-modal__check">{'\u2713'}</span>
            </div>
            <h2 className="fp-cambio-modal__title">Pago Procesado</h2>
            <p className="fp-cambio-modal__desc">La transaccion se completo exitosamente</p>
            <div className="fp-cambio-modal__info">
              <div className="fp-cambio-modal__info-label">CAMBIO A DEVOLVER:</div>
              <div className="fp-cambio-modal__info-amount">${datosCambio.cambio.toFixed(2)}</div>
              <div className="fp-cambio-modal__details">
                <div>
                  <div className="fp-cambio-modal__detail-label">Total de la cuenta:</div>
                  <div className="fp-cambio-modal__detail-value">${datosCambio.total.toFixed(2)}</div>
                </div>
                <div>
                  <div className="fp-cambio-modal__detail-label">Pago con:</div>
                  <div className="fp-cambio-modal__detail-value">${datosCambio.totalPagado.toFixed(2)}</div>
                </div>
              </div>
            </div>
            <button className="fp-cambio-modal__btn" onClick={cerrarModalCambio}>
              <span style={{ fontSize: '20px' }}>{'\u{1F44D}'}</span> Entendido, Continuar
            </button>
            <p className="fp-cambio-modal__hint">Presione Enter o haga clic en el boton para continuar</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormaPago;
